import { useCallback, useState } from 'react';
import { defaultInstance, isAxiosError } from 'src/api/instance';
import { configVars } from 'src/helpers';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import { IListCheckEntry, IListCheckResolutionRequest } from 'src/ts/interfaces/listCheck';
import { listCheckCopy } from '../components/ListChecksPanel/listCheckCopy';

export interface ListCheckResolutionState {
  isSubmitting: boolean;
  resolve: (
    proofId: string,
    checkKey: string,
    body: IListCheckResolutionRequest
  ) => Promise<IListCheckEntry | null>;
  undo: (proofId: string, checkKey: string) => Promise<IListCheckEntry | null>;
}

const resolutionUrl = (proofId: string, checkKey: string): string =>
  `${configVars.server_url}/v2/proofs/${proofId}/list_check_rulings/${checkKey}/resolution`;

export const useListCheckResolution = (): ListCheckResolutionState => {
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(
    async (request: () => Promise<unknown>): Promise<IListCheckEntry | null> => {
      setIsSubmitting(true);
      try {
        return (await request()) as IListCheckEntry;
      } catch (error) {
        const conflict = isAxiosError(error) && error.response?.status === 409;
        showSnackbar(
          VariantType.error,
          conflict ? listCheckCopy.resolutionConflict : listCheckCopy.resolutionFailed,
          SnackTypes.none
        );
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [SnackTypes.none, VariantType.error, showSnackbar]
  );

  const resolve = useCallback(
    (proofId: string, checkKey: string, body: IListCheckResolutionRequest) =>
      submit(() => defaultInstance.post(resolutionUrl(proofId, checkKey), body)),
    [submit]
  );

  const undo = useCallback(
    (proofId: string, checkKey: string) =>
      submit(() => defaultInstance.delete(resolutionUrl(proofId, checkKey))),
    [submit]
  );

  return { isSubmitting, resolve, undo };
};
